import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Spinner, ListGroup } from "react-bootstrap";
import categoryAPI from "../../../../services/categoryapi";

const CategoryCreateForm = ({ show, onHide, editingId, onSuccess }) => {
    const [categoryName, setCategoryName] = useState("");
    const [status, setStatus] = useState(true);
    const [parentID, setParentID] = useState(0);

    const [imageFile, setImageFile] = useState(null);
    const [bannerFile, setBannerFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [bannerPreview, setBannerPreview] = useState("");

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [parentCategories, setParentCategories] = useState([]);
    const [parentInput, setParentInput] = useState("Root / Main Category");
    const [showParentOptions, setShowParentOptions] = useState(false);

    const parentBoxRef = useRef(null);
    const imageInputRef = useRef(null);
    const bannerInputRef = useRef(null);

    const isEditMode = Boolean(editingId);

    const getImageUrl = (path) => {
        if (!path) return "";

        if (path.startsWith("blob:") || path.startsWith("http")) {
            return path;
        }

        const baseUrl = process.env.REACT_APP_IMAGE_URL || "";
        const cleanBase = baseUrl.replace(/\/$/, "");
        const cleanPath = path.replace(/^\//, "");

        if (cleanPath.startsWith("uploads/")) {
            return `${cleanBase}/${cleanPath}`;
        }

        return `${cleanBase}/uploads/${cleanPath}`;
    };

    useEffect(() => {
        if (!show) return;

        resetForm();
        fetchParentCategories("");

        if (isEditMode) {
            fetchCategoryData();
        }
    }, [show, editingId]);

    useEffect(() => {
        if (!show) return;

        const timer = setTimeout(() => {
            const searchText =
                parentInput === "Root / Main Category" ? "" : parentInput;

            fetchParentCategories(searchText);
        }, 300);

        return () => clearTimeout(timer);
    }, [parentInput, show, editingId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                parentBoxRef.current &&
                !parentBoxRef.current.contains(event.target)
            ) {
                setShowParentOptions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        return () => {
            if (imagePreview && imagePreview.startsWith("blob:")) {
                URL.revokeObjectURL(imagePreview);
            }

            if (bannerPreview && bannerPreview.startsWith("blob:")) {
                URL.revokeObjectURL(bannerPreview);
            }
        };
    }, [imagePreview, bannerPreview]);

    const getCategoryLabel = (cat) => {
        if (cat.parent?.categoryName) {
            return `${cat.parent.categoryName} > ${cat.categoryName}`;
        }

        return cat.categoryName;
    };

    const fetchParentCategories = async (search = "") => {
        try {
            const res = await categoryAPI.getAllCategories({
                limit: 500,
                page: 0,
                status: 1,
                search,
                excludeID: editingId || null,
            });

            const list = res.data.result || [];
            setParentCategories(list);

            if (parentID > 0) {
                const selected = list.find(
                    (cat) => Number(cat.categoryID) === Number(parentID)
                );

                if (selected && !showParentOptions) {
                    setParentInput(getCategoryLabel(selected));
                }
            }
        } catch (err) {
            console.error("Failed to fetch parent categories", err);
        }
    };

    const fetchCategoryData = async () => {
        setLoading(true);

        try {
            const res = await categoryAPI.getCategoryById(editingId);
            const data = res.data.category;

            setCategoryName(data.categoryName || "");
            setStatus(Number(data.status) === 1);
            setParentID(Number(data.parentID || 0));

            if (Number(data.parentID || 0) === 0) {
                setParentInput("Root / Main Category");
            } else if (data.parent?.categoryName) {
                setParentInput(data.parent.categoryName);
            }

            setImagePreview(getImageUrl(data.image));
            setBannerPreview(getImageUrl(data.bannerImage));
        } catch (err) {
            console.error("Failed to fetch category data", err);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setCategoryName("");
        setStatus(true);
        setParentID(0);
        setImageFile(null);
        setBannerFile(null);
        setImagePreview("");
        setBannerPreview("");
        setErrors({});
        setParentInput("Root / Main Category");
        setShowParentOptions(false);

        if (imageInputRef.current) imageInputRef.current.value = "";
        if (bannerInputRef.current) bannerInputRef.current.value = "";
    };

    const validateForm = () => {
        const newErrors = {};

        if (!categoryName.trim()) {
            newErrors.categoryName = "Category name is required";
        }

        return newErrors;
    };

    const handleParentInputChange = (e) => {
        const value = e.target.value;

        setParentInput(value);
        setParentID(0);
        setShowParentOptions(true);
    };

    const selectParentCategory = (cat) => {
        if (!cat) {
            setParentID(0);
            setParentInput("Root / Main Category");
        } else {
            setParentID(Number(cat.categoryID));
            setParentInput(getCategoryLabel(cat));
        }

        setShowParentOptions(false);
    };

    const handleImageChange = (e) => {
        const file = e.target.files && e.target.files[0];

        if (!file) return;

        if (imagePreview && imagePreview.startsWith("blob:")) {
            URL.revokeObjectURL(imagePreview);
        }

        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleBannerChange = (e) => {
        const file = e.target.files && e.target.files[0];

        if (!file) return;

        if (bannerPreview && bannerPreview.startsWith("blob:")) {
            URL.revokeObjectURL(bannerPreview);
        }

        setBannerFile(file);
        setBannerPreview(URL.createObjectURL(file));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formErrors = validateForm();

        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();

            formData.append("name", categoryName.trim());
            formData.append("status", status ? 1 : 0);
            formData.append("parentID", parentID || 0);

            if (imageFile) formData.append("category_image", imageFile);
            if (bannerFile) formData.append("banner_image", bannerFile);

            if (isEditMode) {
                await categoryAPI.updateCategory(editingId, formData);
            } else {
                await categoryAPI.createCategory(formData);
            }

            onSuccess();
            onHide();
            resetForm();
        } catch (err) {
            console.error("Error saving category:", err);
            setErrors({ common: "Something went wrong. Please try again." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg" dialogClassName="catalog-modal">
            <Modal.Header closeButton>
                <Modal.Title>{isEditMode ? "Edit Category" : "Create Category"}</Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {errors.common && <div className="text-danger mb-2">{errors.common}</div>}

                    <Form.Group className="mb-3">
                        <Form.Label>Category Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={categoryName}
                            onChange={(e) => setCategoryName(e.target.value)}
                            isInvalid={!!errors.categoryName}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.categoryName}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" ref={parentBoxRef}>
                        <Form.Label>Parent Category</Form.Label>

                        <div style={{ position: "relative" }}>
                            <Form.Control
                                type="text"
                                value={parentInput}
                                placeholder="Search and select parent category..."
                                onFocus={() => {
                                    setShowParentOptions(true);

                                    if (parentInput === "Root / Main Category") {
                                        setParentInput("");
                                    }
                                }}
                                onChange={handleParentInputChange}
                            />

                            {showParentOptions && (
                                <ListGroup
                                    style={{
                                        position: "absolute",
                                        zIndex: 9999,
                                        width: "100%",
                                        maxHeight: "260px",
                                        overflowY: "auto",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    <ListGroup.Item
                                        action
                                        onClick={() => selectParentCategory(null)}
                                    >
                                        Root / Main Category
                                    </ListGroup.Item>

                                    {parentCategories.map((cat) => (
                                        <ListGroup.Item
                                            action
                                            key={cat.categoryID}
                                            onClick={() => selectParentCategory(cat)}
                                        >
                                            {getCategoryLabel(cat)}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Category Image</Form.Label>
                        <Form.Control
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                        />

                        {imagePreview && (
                            <div className="mt-2">
                                <img
                                    src={imagePreview}
                                    alt="Category"
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                    }}
                                    style={{
                                        width: "180px",
                                        height: "120px",
                                        objectFit: "cover",
                                        border: "1px solid #ddd",
                                        borderRadius: "6px",
                                    }}
                                />
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Banner Image</Form.Label>
                        <Form.Control
                            ref={bannerInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleBannerChange}
                        />

                        {bannerPreview && (
                            <div className="mt-2">
                                <img
                                    src={bannerPreview}
                                    alt="Banner"
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                    }}
                                    style={{
                                        width: "250px",
                                        height: "120px",
                                        objectFit: "cover",
                                        border: "1px solid #ddd",
                                        borderRadius: "6px",
                                    }}
                                />
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            id="category-status"
                            label={status ? "Active" : "Inactive"}
                            checked={status}
                            onChange={(e) => setStatus(e.target.checked)}
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Cancel
                    </Button>

                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : isEditMode ? "Update" : "Create"}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CategoryCreateForm;
